import { keepPreviousData, useQuery, type UseQueryOptions } from '@tanstack/react-query';
import {
  listOrganisations,
  listMembers,
  listMembersPage,
  type OrganisationRow,
  type MemberRow,
} from '@/api/endpoints/admin';

/**
 * Query hooks for admin / member management.
 *
 * Keys:
 *   ['admin', 'organisations']
 *   ['organisation', orgId, 'members']
 *
 * Stale time: 60s — admin views aren't real-time.
 */

export function useOrganisations(
  options?: Omit<
    UseQueryOptions<{ organisations: OrganisationRow[]; total: number; invalid: number }>,
    'queryKey' | 'queryFn'
  >,
) {
  return useQuery({
    queryKey: ['admin', 'organisations'],
    queryFn: ({ signal }) => listOrganisations({ signal }),
    staleTime: 60_000,
    ...options,
  });
}

export function useMembers(
  organisationId: string | undefined,
  options?: Omit<UseQueryOptions<MemberRow[]>, 'queryKey' | 'queryFn' | 'enabled'>,
) {
  return useQuery({
    queryKey: ['organisation', organisationId, 'members'],
    queryFn: ({ signal }) => listMembers(organisationId!, { signal }),
    staleTime: 60_000,
    enabled: !!organisationId,
    ...options,
  });
}

/** W6 (SR1-1.2): a server page of members, under the members key so every member mutation refreshes it. */
export function useMembersPage(organisationId: string | undefined, page: { limit: number; offset: number }) {
  return useQuery({
    queryKey: ['organisation', organisationId, 'members', 'page', page],
    queryFn: ({ signal }) => listMembersPage(organisationId!, page, { signal }),
    staleTime: 60_000,
    enabled: !!organisationId,
    placeholderData: keepPreviousData,
  });
}
